import { useCallback, useEffect, useState } from 'react';
import { FiX, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { apiRequest } from '../utils/auth';

const BG_CARD = 'var(--color-surface-secondary)';
const BORDER = 'var(--color-border)';
const TEXT = 'var(--color-text)';
const TEXT_DIM = 'var(--color-muted)';
const RED = 'var(--color-danger)';
const ORANGE = 'var(--color-warning)';

interface CrashBreakdownEntry { label: string; crashes: number; users: number }
interface CrashDailyEntry { date: string; crashes: number; users: number }

interface CrashDetail {
  days: number;
  totals: {
    crashes: number;
    crashingUsers: number;
    activeUsers: number;
    crashFreeUsersRate: number | null;
  };
  daily: CrashDailyEntry[];
  byAppVersion: CrashBreakdownEntry[];
  byPlatform: CrashBreakdownEntry[];
  byDeviceModel: CrashBreakdownEntry[];
  byOsVersion: CrashBreakdownEntry[];
  byFatal: CrashBreakdownEntry[];
  /** Blocks whose GA4 sub-report failed — an empty table for one of these means
   *  "could not load", not "no crashes". */
  unavailable: string[];
}

interface CrashDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Window in days; matches the card on the dashboard. */
  days?: number;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: BG_CARD,
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  width: '100%',
  maxWidth: '860px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

/** One decimal, matching the dashboard card the modal opens from. */
const formatPct = (ratio: number | null | undefined) =>
  ratio == null ? '—' : `${(ratio * 100).toFixed(1)}%`;

const formatDateLabel = (dateStr: string) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function Total({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: 'var(--color-bg)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ color: TEXT_DIM, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color: color || TEXT, fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}

/**
 * One dimension split. `emptyNote` distinguishes the two ways a table can have
 * no rows: nothing crashed, or GA4 could not answer.
 */
function Breakdown({ title, rows, unavailable, emptyNote }: {
  title: string;
  rows: CrashBreakdownEntry[];
  unavailable: boolean;
  emptyNote?: string;
}) {
  return (
    <div style={{ background: 'var(--color-bg)', border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, fontWeight: 600 }}>
        {title}
      </div>
      {unavailable ? (
        <div style={{ padding: '14px', color: ORANGE, fontSize: 12 }}>
          Unavailable — the GA4 report for this breakdown failed.
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '14px', color: TEXT_DIM, fontSize: 12 }}>
          {emptyNote || 'No crashes recorded in this window.'}
        </div>
      ) : (
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['', 'Crashes', 'Users'].map((h, i) => (
                  <th
                    key={h || i}
                    style={{
                      padding: '6px 14px',
                      textAlign: i === 0 ? 'left' : 'right',
                      color: TEXT_DIM,
                      fontWeight: 600,
                      position: 'sticky',
                      top: 0,
                      background: 'var(--color-bg)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} style={{ borderTop: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '6px 14px', color: TEXT, wordBreak: 'break-word' }}>{row.label}</td>
                  <td style={{ padding: '6px 14px', textAlign: 'right', color: RED, fontWeight: 600 }}>
                    {row.crashes.toLocaleString()}
                  </td>
                  <td style={{ padding: '6px 14px', textAlign: 'right', color: TEXT_DIM }}>
                    {row.users.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CrashDetailModal({ isOpen, onClose, days = 30 }: CrashDetailModalProps) {
  const [detail, setDetail] = useState<CrashDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // apiRequest spreads the { success, data } envelope, so the payload's
      // fields arrive at the top level
      const response = await apiRequest<CrashDetail & { success: boolean }>(
        `/api/admin/ga4-crash-detail?days=${days}`,
      );
      setDetail(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load crash detail');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (isOpen) fetchDetail();
  }, [isOpen, fetchDetail]);

  // Escape dismisses the modal, same as the × button and the backdrop click.
  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  const wasUnavailable = (block: string) => !!detail?.unavailable?.includes(block);
  const hasDaily = (detail?.daily?.length ?? 0) > 0;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiAlertTriangle style={{ color: RED, fontSize: 18 }} />
            <div>
              <h2 style={{ margin: 0, color: TEXT, fontSize: 17 }}>Crash Detail</h2>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>
                Firebase Crashlytics <code>app_exception</code> events via GA4 · last {detail?.days ?? days} days
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: TEXT_DIM, fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
          >
            <FiX />
          </button>
        </div>

        <div style={{ padding: '18px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: TEXT_DIM }}>Loading…</div>
          ) : error ? (
            <div style={{ color: RED, background: 'var(--color-surface)', border: `1px solid ${RED}`, borderRadius: 8, padding: '14px' }}>
              {error}
            </div>
          ) : detail ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                <Total label="Crash-free users" value={formatPct(detail.totals.crashFreeUsersRate)} />
                <Total label="Crashes" value={detail.totals.crashes.toLocaleString()} color={RED} />
                <Total label="Users affected" value={detail.totals.crashingUsers.toLocaleString()} color={ORANGE} />
                <Total label="Active users" value={detail.totals.activeUsers.toLocaleString()} />
              </div>

              <div>
                <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Daily crashes</div>
                {wasUnavailable('daily') ? (
                  <div style={{ color: ORANGE, fontSize: 12 }}>Unavailable — the GA4 daily report failed.</div>
                ) : hasDaily ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={detail.daily || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                      <XAxis dataKey="date" tickFormatter={formatDateLabel} stroke={TEXT_DIM} fontSize={11} />
                      <YAxis stroke={TEXT_DIM} fontSize={11} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, color: TEXT }}
                        itemStyle={{ color: TEXT }}
                        labelFormatter={(label: any) => formatDateLabel(String(label))}
                      />
                      <Legend wrapperStyle={{ fontSize: 11, color: TEXT_DIM }} />
                      <Bar dataKey="crashes" name="Crashes" fill={RED} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="users" name="Users affected" fill={ORANGE} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: TEXT_DIM, fontSize: 12 }}>
                    No crashes recorded in this window.
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
                <Breakdown title="By app version" rows={detail.byAppVersion || []} unavailable={wasUnavailable('byAppVersion')} />
                <Breakdown title="By platform" rows={detail.byPlatform || []} unavailable={wasUnavailable('byPlatform')} />
                <Breakdown title="By device model" rows={detail.byDeviceModel || []} unavailable={wasUnavailable('byDeviceModel')} />
                <Breakdown title="By OS version" rows={detail.byOsVersion || []} unavailable={wasUnavailable('byOsVersion')} />
                <Breakdown
                  title="Fatal vs non-fatal"
                  rows={detail.byFatal || []}
                  unavailable={wasUnavailable('byFatal')}
                  emptyNote="Needs a `fatal` custom dimension registered in GA4 — the parameter is on the event, but only registered dimensions are queryable."
                />
              </div>

              <div style={{ color: TEXT_DIM, fontSize: 11, lineHeight: 1.6, borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
                <FiExternalLink style={{ verticalAlign: -2, marginRight: 6 }} />
                GA4 records how many crashes happened and on what, never what threw: exception
                messages, stack traces and issue grouping stay in the Firebase Crashlytics console.
                “Users” columns count distinct users affected, so they do not sum to the window
                total — one user crashing on two app versions is counted under both. Everything here
                is zero until a Crashlytics-enabled build is in users’ hands.
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
